<template>
  <ckeditor :editor="editor" v-model="vmodel" :config="editorConfig" @blur="onBlur" @ready="readyNow" @input="onInput"></ckeditor>
</template>

<script>
import auth from 'utils/auth'
import { SET_SAVE_STATUS } from 'vx/mutations'
import CKEditor from '@ckeditor/ckeditor5-vue'
import AppConstants from '@/support/app-constants'
import { debounce } from 'lodash'
import { mapState, mapActions, mapMutations } from 'vuex'
//import InlineEditor from '@ckeditor/ckeditor5-build-inline';

//Import the specific editor plugins
import InlineEditor from '@ckeditor/ckeditor5-editor-inline/src/inlineeditor'

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter'
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat'
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold'
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder'
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage'
import Heading from '@ckeditor/ckeditor5-heading/src/heading'
import Image from '@ckeditor/ckeditor5-image/src/image'
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption'
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle'
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar'
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload'
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize'
import Indent from '@ckeditor/ckeditor5-indent/src/indent'
import Link from '@ckeditor/ckeditor5-link/src/link'
import List from '@ckeditor/ckeditor5-list/src/list'
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed'
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph'
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice'
import Table from '@ckeditor/ckeditor5-table/src/table'
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar'
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation'
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak'
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter'

export default {
  props: ['content', 'save', 'blur', 'focus'],
  components: {
    ckeditor: CKEditor.component,
  },
  data() {
    return {
      dirty: false,
      editor: InlineEditor,
      editorConfig: {
        width: '500px',
        //extraAllowedContent : 'li[value]',

        plugins: [
          Essentials,
          UploadAdapter,
          Autoformat,
          Bold,
          Italic,
          BlockQuote,
          CKFinder,
          EasyImage,
          Heading,
          Image,
          ImageCaption,
          ImageStyle,
          ImageToolbar,
          ImageUpload,
          ImageResize,
          Indent,
          Link,
          List,
          MediaEmbed,
          Paragraph,
          PasteFromOffice,
          Table,
          TableToolbar,
          TextTransformation,
          PageBreak,
          SimpleUploadAdapter,
        ],

        toolbar: {
          viewportTopOffset: 60,
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            'link',
            'bulletedList',
            'numberedList',
            '|',
            'indent',
            'outdent',
            '|',
            'imageUpload',
            'blockQuote',
            'insertTable',
            'mediaEmbed',
            'undo',
            'redo',
            'pageBreak',
          ],
        },

        image: {
          // Configure the available styles.
          styles: ['alignLeft', 'alignCenter', 'alignRight'],

          // Configure the available image resize options.
          resizeOptions: [
            {
              name: 'imageResize:original',
              label: 'Original',
              value: null,
            },
            {
              name: 'imageResize:50',
              label: '50%',
              value: '50',
            },
            {
              name: 'imageResize:75',
              label: '75%',
              value: '75',
            },
          ],

          // You need to configure the image toolbar, too, so it shows the new style
          // buttons as well as the resize buttons.
          toolbar: [
            'imageStyle:alignLeft',
            'imageStyle:alignCenter',
            'imageStyle:alignRight',
            '|',
            'imageResize',
            '|',
            'imageTextAlternative',
          ],
        },

        simpleUpload: {
          // The URL that the images are uploaded to.
          uploadUrl: process.env.VUE_APP_API_URL + '/ckeditor/upload',

          // Enable the XMLHttpRequest.withCredentials property.
          withCredentials: true,

          // Headers sent along with the XMLHttpRequest to the upload server.
          headers: {
            Authorization: `Bearer ${auth.getToken()}`,
          },
        },

        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'H1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'H2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'H3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'H4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
          ],
        },

        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
        },
        // The configuration of the editor.
      },

      vmodel: this.content,
    }
  },
  mounted () {
  },
  watch: {
    content: function (val, old) {
      if (val !== old && this.vmodel !== val) {
        this.vmodel = val
      }
    },
  },
  methods: {
    onBlur() {
      this.commit().then(() => {
        if (typeof this.blur !== 'undefined') {
          this.blur()
        }
      })
    },

    readyNow(editor) {
      if (this.focus) {
        editor.focus()
      }
    },

    debounceSave: _.debounce(function () {
      this.commit()
    }, 10000),

    commit() {
      if (this.dirty) {
        this.$status.$emit('saving')

        return this.save(this.vmodel).then(() => {
          this.dirty = false
          this.$status.$emit('saved')
        })
      }

      return new Promise((success) => {
        success([])
      })
    },

    onInput() {
      this.dirty = true
      this.$status.$emit('dirty')

      this.debounceSave()
    },
  },
}
</script>
